import { useState } from "react";
import { MdsIconKind } from "@/design-system/components/icon";
import { useAppStore } from "@/store";
import {
  MDSSidebarSection,
  MDSSidebarSectionEmpty,
  MDSSidebarSectionHeader,
  MDSSidebarSectionHeaderSlotName,
  MDSSidebarSectionItem,
  MDSSidebarSectionItemSlotName,
  MDSSidebarSectionSlotName,
} from "@/mds/components/sidebar";
import { observer } from "mobx-react-lite";
import { NoteObservable } from "@/store/note";
import { SectionListTitleWrapper, SeeAllButton } from "./sidebar-contents.style";
import {
  MDSButtonGroup,
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconButtonVariant,
  MDSTooltip,
  MDSTooltipPlacement,
  MDSTextVariant,
  MDSText,
  MDSTextColor,
  MDSTextWeight,
} from "@/mds";
import { EventContext } from "@/domains/metrics/context";
import { motion } from "motion/react";

interface SidebarContentsRecentNotesListProps {
  onClick?: () => void;
  includeTitle?: boolean;
  indent?: boolean;
}

interface SidebarContentsRecentNotesProps {
  onClick?: () => void;
}

export const SidebarContentsRecentNotes = observer(function SidebarContentsRecentNotes({
  onClick,
}: SidebarContentsRecentNotesProps) {
  const { store } = useAppStore();
  const [isExpanded, setIsExpanded] = useState(true);

  const hasRecentNotes =
    store.recentItems.sortedRecentItems.filter(item => item instanceof NoteObservable).length > 0;

  return (
    <MDSSidebarSection isExpanded={isExpanded}>
      <MDSSidebarSectionHeader
        slot={MDSSidebarSectionSlotName.Header}
        label="Notes"
        defaultIconKind={MdsIconKind.Notebook}
        isExpanded={isExpanded}
        onPress={() => setIsExpanded(!isExpanded)}
      >
        <MDSButtonGroup slot={MDSSidebarSectionHeaderSlotName.ButtonsRight}>
          <MDSTooltip config={{ label: "Create note", placement: MDSTooltipPlacement.Top }}>
            <MDSIconButton
              size={MDSIconButtonSize.XSmall}
              iconKind={MdsIconKind.Plus}
              variant={MDSIconButtonVariant.OnChrome}
              onClick={() =>
                store.notes.composeNewNote({ eventContext: EventContext.GlobalActions })
              }
              rounded
            />
          </MDSTooltip>
        </MDSButtonGroup>
      </MDSSidebarSectionHeader>
      {hasRecentNotes && (
        <>
          <SidebarContentsRecentNotesList onClick={onClick} indent />
          <SeeAllButton
            onClick={() => {
              onClick?.();
              store.navigation.goToNotes();
            }}
          >
            See All
          </SeeAllButton>
        </>
      )}
      {!hasRecentNotes && (
        <MDSSidebarSectionEmpty>
          <MDSText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Tertiary}>
            Recent notes appear here
          </MDSText>
        </MDSSidebarSectionEmpty>
      )}
    </MDSSidebarSection>
  );
});

export const SidebarContentsRecentNotesList = observer(function SidebarContentsRecentNotesList({
  onClick,
  includeTitle,
  indent,
}: SidebarContentsRecentNotesListProps) {
  const { store } = useAppStore();
  const recentNotes = store.recentItems.sortedRecentItems
    .filter(item => item instanceof NoteObservable)
    .slice(0, 5);

  return (
    <>
      {includeTitle && (
        <SectionListTitleWrapper>
          <MDSText
            variant={MDSTextVariant.TextXs}
            weight={MDSTextWeight.SemiBold}
            color={MDSTextColor.Secondary}
          >
            Recent Notes
          </MDSText>
        </SectionListTitleWrapper>
      )}
      {recentNotes.map(note => (
        <motion.div key={note.id} layoutId={note.id}>
          <MDSSidebarSectionItem
            key={note.id}
            label={note.title}
            iconKind={MdsIconKind.Document}
            onClick={() => {
              onClick?.();
              store.navigation.goToNote({ noteId: note.id });
            }}
            indent={indent}
          >
            <MDSButtonGroup slot={MDSSidebarSectionItemSlotName.ButtonsRight}>
              <MDSTooltip
                config={{
                  label: note.isFavorited ? "Unpin from sidebar" : "Pin to sidebar",
                  placement: MDSTooltipPlacement.Top,
                }}
              >
                <MDSIconButton
                  size={MDSIconButtonSize.XSmall}
                  iconKind={note.isFavorited ? MdsIconKind.ThumbtackSolid : MdsIconKind.Thumbtack}
                  variant={MDSIconButtonVariant.OnChrome}
                  onClick={() => {
                    onClick?.();
                    note.toggleFavorite();
                  }}
                  rounded
                />
              </MDSTooltip>
            </MDSButtonGroup>
          </MDSSidebarSectionItem>
        </motion.div>
      ))}
    </>
  );
});
